import { Modal, Tabs } from "@mantine/core";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router";
import { useIsMieterstrom } from "../../hooks/useIsMieterstrom";
import { useMissingObjectData } from "../../hooks/useMissingObjectData";
import { usePersons } from "../../hooks/usePersons";
import { TabWithHighlightAndError } from "../../mantine/components/TabWithHighlightAndError/TabWithHighlightAndError";
import { useToast } from "../../mantine/notifications/useToast";
import type {
  BaseWizardProps,
  NestedSaveProps
} from "../../types/wizard.types";
import { ObjectName } from "../../utils/enums";
import { MarktakteurContactInformation } from "../ComponentListContainer/ComponentList/ComponentEditWizard/Components/MarktakteurContactInformation";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { useWizardStack } from "../WizardStack/WizardStackContext";
import type {
  ConnectionCreateFormData,
  ConnectionResponse
} from "./Connection.types";
import { ConnectionForm } from "./ConnectionForm/ConnectionForm";
import type { ConnectionFormData } from "./ConnectionForm/ConnectionForm.types";
import { useConnection } from "./hooks/useConnection";
import { useConnectionMutation } from "./hooks/useConnectionMutation";
import { useMeteringConcepts } from "./hooks/useMeteringConcepts";

const TAB_STAMMDATEN = "stammdaten";
const TAB_KONTAKTDATEN_ANB = "kontaktdaten-anb";
const TAB_KONTAKTDATEN_UENB = "kontaktdaten-uenb";

type ConnectionWizardProps = BaseWizardProps &
  NestedSaveProps<ConnectionResponse> & {
    siteId: number;
    size?: "lg" | "xl";
    stackId?: string;
    variantId: number;
    onClose: () => void;
  };

function ConnectionWizard({
  graphCoordinates = { x: 0, y: 0 },
  isOpen,
  siteId,
  size = "xl",
  stackId,
  variantId,
  onClose,
  ...modeProps
}: ConnectionWizardProps) {
  const { showToast } = useToast();
  const { depth } = useWizardStack();
  const [searchParams, setSearchParams] = useSearchParams();
  const { createMutation, editMutation } = useConnectionMutation(siteId);
  const { data: isMieterstrom, isLoading: isLoadingIsMieterstrom } =
    useIsMieterstrom(siteId);
  const {
    persons,
    isLoading: isLoadingPersons,
    error: personsError
  } = usePersons({ variantId });
  const {
    data: meteringConcepts,
    isLoading: isLoadingMeteringConcepts,
    error: meteringConceptsError
  } = useMeteringConcepts();

  const connectionId =
    modeProps.mode === "edit" ? modeProps.objectId : undefined;

  const {
    data: connection,
    isLoading: isConnectionLoading,
    error: connectionError
  } = useConnection(connectionId);

  const {
    isLoading: isMissingDataLoading,
    error: missingDataError,
    fieldsThatCouldBeMissing,
    shouldFieldBeHighlighted
  } = useMissingObjectData(connectionId, siteId);

  const isEditMode = modeProps.mode === "edit";

  const [highlightedTabs, setHighlightedTabs] = useState<Array<string>>([]);

  const handleChangeNumHighlightedFields = useCallback(
    (numHighlightedFields: number) => {
      setHighlightedTabs(numHighlightedFields > 0 ? [TAB_STAMMDATEN] : []);
    },
    []
  );

  function getVisibleTabs() {
    if (modeProps.context === "nested") {
      return [{ value: TAB_STAMMDATEN, label: "Stammdaten" }];
    }

    const tabs = [{ value: TAB_STAMMDATEN, label: "Stammdaten" }];

    if (connection?.grid_operator_marktakteur) {
      tabs.push({ value: TAB_KONTAKTDATEN_ANB, label: "Kontaktdaten ANB" });
    }

    if (connection?.transmission_system_operator_marktakteur) {
      tabs.push({ value: TAB_KONTAKTDATEN_UENB, label: "Kontaktdaten ÜNB" });
    }

    return tabs;
  }

  function getActiveTab() {
    if (modeProps.context === "nested") {
      return TAB_STAMMDATEN;
    }

    const requestedTab = searchParams.get("tab") ?? TAB_STAMMDATEN;
    const visibleTabs = getVisibleTabs();
    const requestedTabIsVisible = visibleTabs.some(
      (tab) => tab.value === requestedTab
    );
    return requestedTabIsVisible ? requestedTab : TAB_STAMMDATEN;
  }

  const activeTab = getActiveTab();
  const visibleTabs = getVisibleTabs();

  async function handleSubmitCreate(formData: ConnectionCreateFormData) {
    return createMutation.mutateAsync(
      {
        ...formData,
        site: siteId,
        sketch_element: {
          x_position: graphCoordinates.x,
          y_position: graphCoordinates.y
        }
      },
      {
        onSuccess: (response) => {
          if (modeProps.context === "nested") {
            modeProps.onNestedSave(response.data);
          } else {
            showToast({
              type: "success",
              message: "Der Netzverknüpfungspunkt wurde erstellt."
            });
          }
          onClose();
        }
      }
    );
  }

  function handleSubmitUpdate(formData: ConnectionFormData, id: number) {
    return editMutation.mutateAsync(
      { ...formData, id },
      {
        onSuccess: (response) => {
          if (modeProps.context === "nested") {
            modeProps.onNestedSave(response.data);
          } else {
            showToast({
              type: "success",
              message: "Die Änderungen wurden gespeichert."
            });
          }
          onClose();
        }
      }
    );
  }

  function getFormModeProps() {
    if (modeProps.mode === "create") {
      return { mode: "create" as const };
    }

    if (connection) {
      return {
        mode: "edit" as const,
        wizardObject: connection,
        fieldsThatCouldBeMissing,
        shouldFieldBeHighlighted
      };
    }

    return null;
  }

  const formModeProps = getFormModeProps();

  const action = isEditMode ? "bearbeiten" : "erstellen";
  const title = connection?.name
    ? `${connection.name} ${action}`
    : `${ObjectName.Connection} ${action}`;

  const isLoading = isEditMode
    ? isConnectionLoading ||
      isMissingDataLoading ||
      isLoadingPersons ||
      isLoadingMeteringConcepts ||
      isLoadingIsMieterstrom
    : isLoadingIsMieterstrom || isLoadingPersons || isLoadingMeteringConcepts;
  const error = isEditMode
    ? connectionError ||
      missingDataError ||
      personsError ||
      meteringConceptsError
    : (personsError ?? meteringConceptsError);

  return (
    <Modal
      className="ConnectionWizard"
      closeOnEscape={depth === 0}
      opened={isOpen}
      size={size}
      stackId={stackId}
      title={title}
      onClose={onClose}
    >
      <LoadOrError error={error} loading={isLoading}>
        <Tabs
          value={activeTab}
          onChange={(newTab) => {
            if (!newTab) {
              return;
            }

            searchParams.set("tab", newTab);
            setSearchParams(searchParams);
          }}
        >
          {isEditMode && modeProps.context !== "nested" && (
            <Tabs.List>
              {visibleTabs.map((tab) => (
                <TabWithHighlightAndError
                  highlighted={highlightedTabs.includes(tab.value)}
                  key={tab.value}
                  value={tab.value}
                >
                  {tab.label}
                </TabWithHighlightAndError>
              ))}
            </Tabs.List>
          )}
          <Tabs.Panel value={TAB_STAMMDATEN}>
            {formModeProps && meteringConcepts && (
              <ConnectionForm
                isMieterstrom={isMieterstrom ?? false}
                meteringConcepts={meteringConcepts}
                persons={persons}
                onCancel={onClose}
                onChangeNumHighlightedFields={handleChangeNumHighlightedFields}
                onSubmitCreate={handleSubmitCreate}
                onSubmitUpdate={handleSubmitUpdate}
                {...formModeProps}
              />
            )}
          </Tabs.Panel>
          {connection?.grid_operator_marktakteur && (
            <Tabs.Panel value={TAB_KONTAKTDATEN_ANB}>
              <MarktakteurContactInformation
                marktakteur={connection.grid_operator_marktakteur}
              />
            </Tabs.Panel>
          )}
          {connection?.transmission_system_operator_marktakteur && (
            <Tabs.Panel value={TAB_KONTAKTDATEN_UENB}>
              <MarktakteurContactInformation
                marktakteur={
                  connection.transmission_system_operator_marktakteur
                }
              />
            </Tabs.Panel>
          )}
        </Tabs>
      </LoadOrError>
    </Modal>
  );
}

export { ConnectionWizard, ConnectionWizardProps };
